import * as THREE from 'three';
import type { Point } from '../../../shared/game/world-types.js';

/** How many places can push the grass at once: the animals walking about plus the fading trail behind each. */
export const PUSHERS = 24;
/** Shared by every wind material that lets itself be walked through: x, z, reach in metres and strength, one `vec4` each. */
export const pushers = { value: Array.from({ length: PUSHERS }, () => new THREE.Vector4(0, 0, 1, 0)) };

const UNIT_REACH = 1.15, TRAIL_REACH = .8, TRAIL_SECONDS = 4.5, TRAIL_SPACING = .9, MAX_UNITS = 8;

/**
 * Grass and low scrub bend away from whoever walks through them and spring back a few seconds after they pass. The
 * scene tells this where the walkers are each frame; it keeps the walkers' current places and a short trail of where each
 * has just been, as `PUSHERS` vectors the wind shader reads. Purely visual: nothing here touches collision or rules.
 */
export class TrampleTrail {
  private trail: { x: number; z: number; age: number }[] = [];
  private last = new Map<string, Point>();
  private near: { id: string; x: number; z: number }[] = [];

  /** `walkers` are the units on show, with a stable id each; only the nearest `MAX_UNITS` to `focus` are used. */
  update(dt: number, walkers: { id: string; x: number; z: number }[], focus: Point) {
    const near = this.near; near.length = 0;
    for (const walker of walkers) near.push(walker);
    near.sort((a, b) => Math.hypot(a.x - focus.x, a.z - focus.z) - Math.hypot(b.x - focus.x, b.z - focus.z));
    near.length = Math.min(near.length, MAX_UNITS);
    for (const entry of this.trail) entry.age += dt;
    let write = 0;
    for (const entry of this.trail) if (entry.age < TRAIL_SECONDS) this.trail[write++] = entry;
    this.trail.length = write;
    for (const walker of near) {
      const previous = this.last.get(walker.id);
      if (!previous || Math.hypot(walker.x - previous.x, walker.z - previous.z) > TRAIL_SPACING) {
        // A jump (a teleport, a rewound game) leaves no trail between its two ends.
        if (previous && Math.hypot(walker.x - previous.x, walker.z - previous.z) < 6) this.trail.push({ x: previous.x, z: previous.z, age: 0 });
        this.last.set(walker.id, { x: walker.x, z: walker.z });
      }
    }
    // The newest trail points fill what the walkers leave free.
    const free = PUSHERS - near.length, trailStart = Math.max(0, this.trail.length - free);
    for (let i = 0; i < near.length; i++) {
      const walker = near[i]; pushers.value[i].set(walker.x, walker.z, UNIT_REACH, 1);
    }
    let index = near.length;
    for (let i = trailStart; i < this.trail.length; i++) {
      const entry = this.trail[i];
      pushers.value[index++].set(entry.x, entry.z, TRAIL_REACH, (1 - entry.age / TRAIL_SECONDS) ** 1.6 * .8);
    }
    for (let i = index; i < PUSHERS; i++) pushers.value[i].set(0, 0, 1, 0);
  }

  /** For tests: the strength each slot currently pushes with. */
  get strengths() { return pushers.value.map(vector => vector.w); }
}
